import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { apibaseurl } from "../../environment";
import axios, { AxiosResponse } from "axios";
interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  payload?: Record<string, any> | null;
  autoCall?: boolean;
  dependencies?: any[];
  responseType?: "json" | "blob" | "arraybuffer";

  // ✅ NEW: control global error handling (optional)
  handleGlobalError?: boolean;
}

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: any;
  callApi: (
    payload?: Record<string, any> | FormData,
    customUrl?: string,
  ) => Promise<T | undefined>;
}

function useApi<T = any>(
  endpoint: string,
  {
    method = "GET",
    payload = null,
    autoCall = true,
    dependencies = [],
    responseType = "json",
    handleGlobalError = true, // ✅ default true
  }: ApiOptions = {},
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(autoCall);
  const [error, setError] = useState<any>(null);
  const navigate = useNavigate();

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const callApi = useCallback(
    async (
      customPayload = payload,
      customUrl?: string,
    ): Promise<T | undefined> => {
      setLoading(true);

      try {
        const isFormData = customPayload instanceof FormData;

        const config = {
          headers: {
            // ✅ FIX: Do not set content-type for FormData (browser sets it)
            ...(isFormData ? {} : { "Content-Type": "application/json" }),
            ...getAuthHeader(),
          },
          responseType,
          data: method === "DELETE" ? customPayload : undefined,
        };

        const finalUrl = `${apibaseurl}${customUrl ?? endpoint}`;
        let response: AxiosResponse<any>;

        if (method === "POST") {
          response = await axios.post(finalUrl, customPayload, config);
        } else if (method === "PUT") {
          response = await axios.put(finalUrl, customPayload, config);
        } else if (method === "PATCH") {
          response = await axios.patch(finalUrl, customPayload, config);
        } else if (method === "DELETE") {
          response = await axios.delete(finalUrl, config);
        } else {
          response = await axios.get(finalUrl, config);
        }

        setData(response.data);
        setError(null);

        return response.data;
      } catch (err: any) {
        setError(err);

        // ✅ FIX 1: Do NOT force navigation for all APIs
        // Let component decide what to do (important for upload APIs)
        if (axios.isAxiosError(err)) {
          const status = err.response?.status;

          // ✅ Keep only AUTH handling global
          if (status === 401) {
            localStorage.removeItem("token");
            navigate("/login");
          }

          // ✅ OPTIONAL: Only navigate if explicitly allowed
          if (handleGlobalError) {
            if (status === 500 || err.message === "Network Error") {
              console.log("Global API Error:", err);
              navigate("/error-500"); // controlled now
            }
          }
        }

        // ✅ FIX 2: Throw error so component can handle (SweetAlert etc.)
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [endpoint, method, payload, navigate, handleGlobalError],
  );

  useEffect(() => {
    if (autoCall) {
      callApi();
    }
  }, [...dependencies, callApi]);

  return { data, loading, error, callApi };
}

export default useApi;
